/**
 * @fileoverview App Access Gate
 * 
 * Wraps /app routes and controls access based on admin-configured mode:
 * - open: normal access
 * - closed: redirect to marketing site
 * - password: show password entry screen
 * 
 * Native (Capacitor) apps always bypass the gate.
 */

import { useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAppAccessConfig } from '@/hooks/useAppAccessConfig';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';

const SESSION_KEY = 'app-access-granted';

interface AppAccessGateProps {
  children: ReactNode;
}

export function AppAccessGate({ children }: AppAccessGateProps) {
  const { mode, password, isLoading } = useAppAccessConfig();
  const [inputPassword, setInputPassword] = useState('');
  const [error, setError] = useState(false);
  const [granted, setGranted] = useState(() => sessionStorage.getItem(SESSION_KEY) === 'true');

  // Native apps always bypass
  if (Capacitor.isNativePlatform()) return <>{children}</>;

  // While loading config, show nothing (prevent flash)
  if (isLoading) return null;

  if (mode === 'open' || granted) return <>{children}</>;

  if (mode === 'closed') return <Navigate to="/" replace />;

  // Password mode
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputPassword === password) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      setGranted(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">Access Required</h1>
          <p className="text-sm text-muted-foreground">
            Enter the password to access the app.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="password"
            placeholder="Password"
            value={inputPassword}
            onChange={(e) => { setInputPassword(e.target.value); setError(false); }}
            autoFocus
          />
          {error && (
            <p className="text-sm text-destructive">Incorrect password</p>
          )}
          <Button type="submit" className="w-full">
            Enter
          </Button>
        </form>
      </div>
    </div>
  );
}
